import { useCallback, useEffect, useRef, useState } from 'react';
import Breadcrumb from '@/components/common/Breadcrumb';
import { fetchMagazines } from '@/api/magazineCatchApi';
import type { MagazineListItem } from '@/types/api.zod';
import { buildCoverImageUrl } from '@/utils/coverImage';
import { logger } from '@/utils/logger';
import MagazineCatchEditModal from './MagazineCatchEditModal';

/**
 * マガジンキャッチ管理 (一覧)
 *
 * - データソース: `GET /books/magazines` (UserHttpApi, 認証不要)
 * - 並び順: API レスポンス順をそのまま尊重する (Lambda 側で
 *   `latest_release_at` 降順 + 同日優先度ソート済み)
 * - 各行の「キャッチ変更」ボタンで `MagazineCatchEditModal` を開く (#233)
 */
const MagazineCatchList = () => {
  const [magazines, setMagazines] = useState<MagazineListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // 編集中のマガジン (モーダル開閉 + 受け渡し)
  const [editingMagazine, setEditingMagazine] = useState<MagazineListItem | null>(null);
  // 進行中の取得を中断するための AbortController。再取得 (再読み込み / モーダル更新後) や
  // アンマウント時に前回分を abort し、完了後の state 更新の競合・警告を防ぐ。
  const abortRef = useRef<AbortController | null>(null);

  const loadMagazines = useCallback(async () => {
    // 前回の取得が進行中なら中断する (連打・モーダル更新直後の競合を防ぐ)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchMagazines(signal);
      if (signal.aborted) return;
      setMagazines(data.magazines);
    } catch (err) {
      // 中断時はそれ以降の state 更新を行わない (アンマウント後の警告 / 競合を防ぐ)
      if (signal.aborted) return;
      logger.logError('Failed to fetch magazines', err);
      setError('マガジン一覧の取得に失敗しました。時間をおいて再度お試しください。');
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void loadMagazines();
    return () => abortRef.current?.abort();
  }, [loadMagazines]);

  const handleEditCatchClick = (magazine: MagazineListItem) => {
    setEditingMagazine(magazine);
    logger.logInfo(`マガジンキャッチ編集モーダルを開く (title_name=${magazine.title_name})`);
  };

  const handleModalClose = () => {
    setEditingMagazine(null);
  };

  const handleModalUpdated = () => {
    void loadMagazines();
  };

  const isModalOpen = editingMagazine !== null;

  return (
    <div>
      <Breadcrumb />
      <div className="card max-w-5xl mx-auto" aria-hidden={isModalOpen ? 'true' : undefined}>
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-admin-text">マガジンキャッチ管理</h1>
          <button
            type="button"
            onClick={() => void loadMagazines()}
            disabled={loading || isModalOpen}
            className="px-4 py-2 text-sm border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {loading ? '読み込み中...' : '再読み込み'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div
            role="alert"
            className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4"
          >
            {error}
          </div>
        )}

        {/* Loading (initial) */}
        {loading && magazines.length === 0 && !error && (
          <div className="py-12 text-center text-admin-textLight">読み込み中...</div>
        )}

        {/* Empty */}
        {!loading && !error && magazines.length === 0 && (
          <div className="py-12 text-center text-admin-textLight">
            マガジンが登録されていません。
          </div>
        )}

        {/* List */}
        {magazines.length > 0 && (
          <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden">
            {magazines.map((magazine) => (
              <MagazineCatchRow
                key={magazine.title_name}
                magazine={magazine}
                onEditCatchClick={handleEditCatchClick}
                disabled={isModalOpen}
              />
            ))}
          </ul>
        )}
      </div>

      <MagazineCatchEditModal
        magazine={editingMagazine}
        onClose={handleModalClose}
        onUpdated={handleModalUpdated}
      />
    </div>
  );
};

interface MagazineCatchRowProps {
  magazine: MagazineListItem;
  onEditCatchClick: (magazine: MagazineListItem) => void;
  disabled: boolean;
}

const MagazineCatchRow = ({ magazine, onEditCatchClick, disabled }: MagazineCatchRowProps) => {
  return (
    <li className="flex items-start gap-4 p-4 bg-white hover:bg-gray-50 transition-colors">
      <MagazineThumbnail
        coverImage={magazine.cover_image}
        magazineTitle={magazine.magazine_title}
      />
      <div className="flex-1 min-w-0">
        <h2 className="text-base font-semibold text-admin-text truncate">
          {magazine.magazine_title}
        </h2>
        {magazine.magazine_series_title && (
          <p className="text-xs text-admin-textLight mt-0.5 truncate">
            <span className="sr-only">シリーズ名: </span>
            {magazine.magazine_series_title}
          </p>
        )}
        <p className="text-sm text-admin-text mt-2 break-words">
          <span className="text-admin-textLight">現在のキャッチ: </span>
          {magazine.magazine_catch.length > 0 ? (
            magazine.magazine_catch
          ) : (
            <span className="text-admin-textLight">（未設定）</span>
          )}
        </p>
      </div>
      <div className="shrink-0">
        <button
          type="button"
          onClick={() => onEditCatchClick(magazine)}
          disabled={disabled}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`${magazine.magazine_title} のキャッチを変更`}
        >
          キャッチ変更
        </button>
      </div>
    </li>
  );
};

interface MagazineThumbnailProps {
  coverImage: string | undefined;
  magazineTitle: string;
}

/**
 * 64×96px のサムネイル。画像が無い or 読み込み失敗時は灰色のフォールバックを表示する。
 */
const MagazineThumbnail = ({ coverImage, magazineTitle }: MagazineThumbnailProps) => {
  const [imageFailed, setImageFailed] = useState<boolean>(false);
  const resolvedUrl = buildCoverImageUrl(coverImage);
  const showImage = !!resolvedUrl && !imageFailed;

  return (
    <div
      className="shrink-0 w-16 h-24 bg-gray-100 border border-gray-200 rounded overflow-hidden flex items-center justify-center"
      aria-hidden={!showImage ? 'true' : undefined}
    >
      {showImage ? (
        <img
          src={resolvedUrl}
          alt={`${magazineTitle} の表紙`}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <span className="text-xs text-admin-textLight">No Image</span>
      )}
    </div>
  );
};

export default MagazineCatchList;
